import React, { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Row, Col, Button } from "reactstrap";
import FormInput from "../../../form-components/InputComponent";
import FormSelect from "../../../form-components/SelectComponent";
import { useNavigate, useParams } from "react-router-dom";
import sitebreakdownMasterApis from "../../../../api/master/sitebreakdownMaster";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { ReactTyped } from "react-typed";
import useSequence from "../../../../hooks/useSequence";
interface SelectOptions {
  label: string;
  value: string;
}

const BasicInformation: React.FC = (props: any) => {
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasSequence } = useSequence(162);
  const siteProjectsCache = useRef<any[]>([]);
  const isDropdownFetched = useRef(false);
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const [loading, setLoading] = useState(false);
  const [siteOptions, setSiteOptions] = useState<SelectOptions[]>([]);
  const [projectTypeOptions, setProjectTypeOptions] = useState<SelectOptions[]>(
    []
  );

  const [projectNameDisabled, setProjectNameDisabled] = useState(false);
  const [projectTypeDisabled, setProjectTypeDisabled] = useState(false);
  const [existingProjectInfo, setExistingProjectInfo] = useState<any>(null);
  const [fetchingData, setFetchingData] = useState(true);

  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const allSites: any = [];
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          allSites.push(item.siteID);
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSiteOptions([..._siteOptions]);
      }
    }
  }, [currentUser]);

  const fetchDropdownData = async () => {
    try {
      setFetchingData(true);
      // Fetch project types dropdown
      const typesResponse = await sitebreakdownMasterApis.fetchTypeDropDown();
      if (typesResponse && typesResponse.status && typesResponse.data) {
        const typeOptionsData = typesResponse.data.map((type: any) => ({
          label: type.typeName,
          value: type.id.toString(),
        }));
        setProjectTypeOptions(typeOptionsData);
      }
    } catch (error) {
      Swal.fire("Error", "Failed to load dropdown data", "error");
    } finally {
      setFetchingData(false);
    }
  };

  const fetchSiteDetails = async (siteId: any) => {
    try {
      setLoading(true);
      const response = await sitebreakdownMasterApis.fetchSiteBreakDownById(
        siteId
      );
      if (response && response.status) {
        const data = response.data;
        setValue("siteStructureSequenceId", data.siteStructureSequenceId);
        setValue("siteName", data.siteName);
        setValue("projectName", data.projectName || ""); // Set empty if no project name
        setValue("projectType", data.projectType);
        setValue("status", data.status);

        // Set dynamic fields for later steps
        if (data.villas) setValue("villas", data.villas);
        if (data.towers) setValue("towers", data.towers);
        if (data.blocks) setValue("blocks", data.blocks);
        if (data.phases) setValue("phases", data.phases);
        if (data.flats) setValue("flats", data.flats);
        if (data.shops) setValue("shops", data.shops);
        if (data.milestones) setValue("milestones", data.milestones);
        if (data.totalVillas) setValue("totalVillas", data.totalVillas);
        if (data.numberOfTowers)
          setValue("numberOfTowers", data.numberOfTowers);
        if (data.numberOfBlocks)
          setValue("numberOfBlocks", data.numberOfBlocks);
        if (data.numberOfPhases)
          setValue("numberOfPhases", data.numberOfPhases);
        if (data.captureFlatNumbers)
          setValue("captureFlatNumbers", data.captureFlatNumbers);
        if (data.captureShopNumbers)
          setValue("captureShopNumbers", data.captureShopNumbers);
        if (data.captureMilestones)
          setValue("captureMilestones", data.captureMilestones);
      }
    } catch (error) {
      Swal.fire("Error", "Failed to load site structure details", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteProjects = async (siteId: number) => {
    try {
      if (isDropdownFetched.current) {
        return siteProjectsCache.current.find((p) => p.siteId === siteId) || null;
      }
      const res = await sitebreakdownMasterApis.fetchSiteBreakDownDropDown();
      if (res?.status) {
        siteProjectsCache.current = res.data;
        isDropdownFetched.current = true; // BLOCK FUTURE CALLS
        return res.data.find((p: any) => p.siteId === siteId) || null;
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  };


  const handleSiteChange = async (value: string) => {
    const selectedSiteId = Number(value);
    setValue("siteName", selectedSiteId);

    const existingProject = await fetchSiteProjects(selectedSiteId);

    if (existingProject) {
      setExistingProjectInfo(existingProject);
      setProjectNameDisabled(true);
      setProjectTypeDisabled(true);

      setValue("projectName", "");
      setValue("projectType", "");
    } else {
      setExistingProjectInfo(null);
      setProjectNameDisabled(false);
      setProjectTypeDisabled(false);

      // Clear the project name field - NO DEFAULT VALUE
      setValue("projectName", "");
    }
  };

  useEffect(() => {
    fetchDropdownData();
    if (id) {
      fetchSiteDetails(id);
    } else {
      // When creating new project (not editing), ensure no default values
      setValue("projectName", "");
    }
  }, [id]);

  const projectType = watch("projectType");
  const siteName = watch("siteName");

  const handleProjectTypeChange = (value: string) => {
    const prevProjectType = sessionStorage.getItem("prevProjectType");
    const hasInteracted = sessionStorage.getItem("hasChangedProjectType");

    if (!prevProjectType) {
      sessionStorage.setItem("prevProjectType", value);
      setValue("projectType", value);
      return;
    }

    if (String(prevProjectType) !== String(value)) {
      if (hasInteracted) {
        Swal.fire({
          title: "Are you sure?",
          text: "Changing the Project Type will discard all existing structure details you have added.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, Change It",
          cancelButtonText: "No, Keep Current",
          allowOutsideClick: false,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-secondary ms-2",
          },
        }).then((result) => {
          if (result.isConfirmed) {
            sessionStorage.setItem("shouldClearTable", "true");
            sessionStorage.setItem("prevProjectType", value);
            setValue("projectType", value);
            resetStructureFields();
          } else {
            setValue("projectType", prevProjectType);
          }
        });
      } else {
        sessionStorage.setItem("hasChangedProjectType", "true");
        sessionStorage.setItem("shouldClearTable", "true");
        sessionStorage.setItem("prevProjectType", value);
        setValue("projectType", value);
        resetStructureFields();
      }
    }
  };

  const resetStructureFields = () => {
    // Reset structure fields when project type changes
    setValue("totalVillas", "");
    setValue("numberOfTowers", "");
    setValue("numberOfBlocks", "");
    setValue("numberOfPhases", "");
    setValue("villas", []);
    setValue("towers", []);
    setValue("blocks", []);
    setValue("phases", []);
    setValue("flats", []);
    setValue("shops", []);
    setValue("milestones", []);
    setValue("captureFlatNumbers", "NO");
    setValue("captureShopNumbers", "NO");
    setValue("captureMilestones", "NO");
  };

  useEffect(() => {
    setValue("hasSequence", Boolean(hasSequence));
  }, [hasSequence]);

  return (
    <form
      className="content clearfix"
      onSubmit={handleSubmit(() => {
        props.next();
      })}
    >
      <Row className="gy-4">
        {!hasSequence && (
          <Col lg="4">
            <FormInput
              name="siteStructureSequenceId"
              label="Site Structure ID"
              type="text"
              placeholder="Site Structure ID"
              required={false}
              // disabled={true}
              pattern={/^[a-zA-Z0-9./_-]+$/}
              maxLength={30}
            />
          </Col>
        )}

        <Col lg="4">
          <FormSelect
            name="siteName"
            label="Select Site"
            options={siteOptions}
            required={true}
            placeholder={fetchingData ? "Loading sites..." : "Select Site"}
            disabled={!!id || fetchingData}
            onChange={handleSiteChange}
          />
          {existingProjectInfo && (
            <div
              id="typedWrapper"
              className="mt-1"
              style={{
                fontSize: "12px",
                color: "red",
                pointerEvents: "auto",
              }}
            >
              <ReactTyped
                strings={[
                  `Project already exists for this site. <a id="viewProjectLink" style="color:#0d6efd; font-weight:bold; cursor:pointer;">Click to view project</a>`,
                ]}
                typeSpeed={20}
                showCursor={false}
                contentType="html"
                onComplete={() => {
                  const link = document.getElementById("viewProjectLink");
                  link?.addEventListener("click", () => {
                    navigate(`/sitebreakdown-view/${existingProjectInfo.id}`);
                  });
                }}
              />
            </div>
          )}
        </Col>

        <Col lg="4">
          <FormInput
            name="projectName"
            label="Project Name"
            type="text"
            placeholder="Enter Project Name"
            required={true}
            maxLength={100}
            disabled={!!id || projectNameDisabled}
          />
        </Col>

        <Col lg="4">
          <FormSelect
            name="projectType"
            label="Project Type"
            options={projectTypeOptions}
            required={true}
            placeholder={
              fetchingData ? "Loading project types..." : "Select Project Type"
            }
            disabled={!!id || projectTypeDisabled || fetchingData}
            onChange={(value: any) => handleProjectTypeChange(value)}
          />
        </Col>
      </Row>

      {(loading || fetchingData) && (
        <div className="text-center mt-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <div className="actions clearfix">
        <ul>
          <li>
            <Button
              color="primary"
              type="submit"
              disabled={loading || fetchingData || !projectType}
            >
              Next
            </Button>
          </li>
        </ul>
      </div>
    </form>
  );
};

export default React.memo(BasicInformation);